<template>
  <div class="tap-tap">
    <IconLeeaTap class="tap-tap-icon-back" :height="farming ? max : current"></IconLeeaTap>
    <div class="tap-tap-hint" :class="{ hidden: farming || current > 0 }">TAP</div>
    <IconLeea @click="tap" class="tap-tap-icon-front"></IconLeea>
    <div
      v-for="plusOne in plusOnes"
      :key="plusOne.id"
      class="plus-one"
      :style="{ top: `${plusOne.y - 40}px`, left: `${plusOne.x + 20}px` }"
    >
      +1 <IconLP />
    </div>
  </div>
</template>
<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'
import IconLeea from '@/components/Icons/IconLeea.vue'
import IconLeeaTap from './Icons/IconLeeaTap.vue'
import { hapticService } from '@/services/haptic.service'
import IconLP from './Icons/IconLP.vue'
import { useUserStore } from '@/stores/user'

const userStore = useUserStore()
const props = defineProps(['farming'])

const max = 48
const current = ref(0)
const intervalTime = 100
let intervalId: number
let decreateRate: number
let increaseRate = 2

const emit = defineEmits(['success'])

const plusOnes = ref<{ x: number; y: number; id: number }[]>([])
let plusOneId = 0
const clearCurrent = () => {
  current.value = 0
}

defineExpose({
  clearCurrent
})

onMounted(() => {
  if (props.farming) current.value = max

  decreateRate = 48 / (100 * 3)
  intervalId = window.setInterval(() => {
    if (props.farming) return
    current.value -= decreateRate
    current.value = Math.max(0, current.value)
  }, intervalTime)
})

onUnmounted(() => {
  if (intervalId) clearInterval(intervalId)
})

const tap = (event: MouseEvent) => {
  current.value += increaseRate

  if (current.value >= max && !props.farming) {
    emit('success')
  }
  if (!props.farming) hapticService.vibrate()

  current.value = Math.max(0, Math.min(current.value + increaseRate, max))

  // Add new +1 with unique id and position
  userStore.addTap()
  const id = plusOneId++
  plusOnes.value.push({ x: event.clientX, y: event.clientY, id })

  // Remove +1 after 1 second
  setTimeout(() => {
    plusOnes.value = plusOnes.value.filter((plusOne) => plusOne.id !== id)
  }, 1000)
}
</script>

<style lang="scss">
.tap-tap {
  .tap-tap-icon-front,
  .tap-tap-icon-back {
    position: absolute;
    width: 120px;
    height: 120px;
    top: calc(50% - 60px);
    left: calc(50% - 60px);
  }

  .tap-tap-icon-front {
    padding: 20px;
    box-sizing: content-box;
    top: calc(50% - 80px);
    left: calc(50% - 80px);
  }

  .tap-tap-icon-back {
    transform: rotate(180deg);
    path {
      fill: var(--primary-color);
    }
  }
  .tap-tap-icon-front {
    path {
      fill: transparent;
      stroke-width: 0.3px;
      stroke: var(--primary-light-color);
    }
  }
  .tap-tap-hint {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Forma DJR Display';
    font-weight: 600;
    letter-spacing: 0.64px;
    color: var(--primary-light-color);
    width: 120px;
    height: 120px;
    top: calc(50% - 60px);
    left: calc(50% - 60px);
    animation: fade 2s infinite linear;
  }

  .plus-one {
    pointer-events: none;
    position: absolute;
    color: var(--primary-color);
    font-weight: bold;
    font-size: 24px;
    animation: fade-out 1s forwards;
    font-family: 'Forma DJR Display';
    font-size: 36px;
    display: flex;
    gap: 10px;

    text-shadow:
      -1px -1px 0 white,
      1px -1px 0 white,
      -1px 1px 0 white,
      1px 1px 0 white;

    .icon {
      fill: var(--primary-color);
      stroke: white;
      stroke-width: 1px;
    }
  }

  .hidden {
    display: none;
  }
  .front .icon svg path {
    fill: none;
  }

  @keyframes fade-out {
    0% {
      transform: translateY(0);
      opacity: 1;
    }
    100% {
      transform: translateY(-40px);
      opacity: 0;
    }
  }

  @keyframes fade {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
}
</style>
